import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';

interface UserProfileDropdownProps {
  user: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
}

export const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const initials = (user.name ?? '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignore */ }
    navigate('/login');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-lg transition-all hover:shadow-xl hover:ring-2 hover:ring-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        aria-label="User profile menu"
        title={user.name}
      >
        {user.avatar ? (
          <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <span className="text-sm font-semibold">{initials}</span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-72 origin-top-right rounded-lg border border-gray-200 bg-white shadow-xl">
          <div className="border-b border-gray-200 px-6 py-6">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} className="h-16 w-16 rounded-full object-cover" />
                ) : (
                  <span className="text-lg font-semibold">{initials}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-sm font-semibold text-gray-900">{user.name}</h3>
                <p className="truncate text-xs text-gray-500">{user.email}</p>
              </div>
            </div>
          </div>
          <div className="py-2">
            <button
              onClick={() => { setIsOpen(false); navigate('/settings'); }}
              className="flex w-full items-center gap-3 px-6 py-3 text-sm text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none"
            >
              <Settings size={18} className="text-gray-400" />
              <span>Settings</span>
            </button>
            <div className="my-2 border-t border-gray-200" />
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-6 py-3 text-sm text-red-600 transition-colors hover:bg-red-50 focus:outline-none"
            >
              <LogOut size={18} className="text-red-400" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
